import { navigate } from "@reach/router";
import { observer } from "mobx-react";
import React, { useEffect, useState, useRef } from "react";
import { Checkbox } from "../../components/-common";
import { useBreakpoints } from "../../hooks/useBreakpoints";
import AuthStore from "../../stores/AuthStore";
import CartStore from "../../stores/CartStore";
import MaterialsStore, {
  filterOutLateFeeProducts,
  filterOutShippingProducts,
  filterOutTaxProducts
} from "../../stores/MaterialsStore";
import UserStore from "../../stores/UserStore";
import * as Sentry from "@sentry/react";
import RegistrationService from "../../services/RegistrationService";
import AZTaxItem from "./AZTaxItem";
import "./Checkout.scss";
import CheckoutButton from "./CheckoutButton";
import CouponItem from "./CouponItem";
import EnrollmentItem from "./EnrollmentItem";
import LateFeeItem from "./LateFeeItem";
import PaymentInfoSection, { NEW_CARD_OPTION_LABEL } from "./PaymentInfoSection";
import ShippingItem from "./ShippingItem";
import useCardValid from "./useCardValid";

const Checkout = observer(() => {
  const { isMobile } = useBreakpoints();

  const { authDetermined, authenticated } = AuthStore || {};
  const { enrollmentsReadyForCheckout } = CartStore || {};

  const hasEnrollmentsForCheckout = !!enrollmentsReadyForCheckout?.length;
  useEffect(() => {
    if (authDetermined) {
      if (!authenticated || !hasEnrollmentsForCheckout) navigate("/");
    }
  }, [authDetermined, authenticated, hasEnrollmentsForCheckout]);

  const { rawCards, cards } = UserStore || {};
  useEffect(() => {
    if (!rawCards) UserStore?.fetchCardsForUser();
  }, [rawCards]);

  const enrollmentCourseIds = enrollmentsReadyForCheckout?.map(({ class: classObject }) => classObject?.courseId);
  useEffect(() => {
    if (enrollmentCourseIds?.length) {
      enrollmentCourseIds.forEach(courseId => MaterialsStore.fetchMaterialsForCourse(courseId));
    }
  }, [enrollmentCourseIds]);

  const [selectedCard, setSelectedCard] = useState();
  const [enteringNewCard, setEnteringNewCard] = useState(false);
  const [newCardDetails, setNewCardDetails] = useState({});
  const cardValid = useCardValid({ cardDetails: newCardDetails, setCardDetails: setNewCardDetails });
  const [processing, setProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState();
  const [errorType, setErrorType] = useState();
  const [canRetry, setCanRetry] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState();
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const updateAgreeToTerms = ([newValue]) => setAgreeToTerms(newValue);
  const [agreeToMaterialsDelivery, setAgreeToMaterialsDelivery] = useState(false);
  const updateAgreeToMaterialsDelivery = ([newValue]) => setAgreeToMaterialsDelivery(newValue);
  const [keapCreditCardId, setKeapCreditCardId] = useState(null);

  // Track last click time to prevent double-clicks
  const lastClickTimeRef = useRef(0);

  // Check for existing registration session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const existingSession = await RegistrationService.restoreSession();
      if (existingSession) {
        if (existingSession.processing) {
          setRegistrationStatus("Registration in progress...");
          setProcessing(true);
          // Poll for completion using transaction ID
          const finalStatus = await RegistrationService.pollRegistrationStatus();
          if (finalStatus.success) {
            // Navigate to thank you page
            handleSuccessfulRegistration(finalStatus);
          } else {
            setCheckoutError(finalStatus.userMessage);
            setCanRetry(finalStatus.canRetry || false);
            setProcessing(false);
          }
        } else if (existingSession.success) {
          // Registration was completed - navigate to thank you
          handleSuccessfulRegistration(existingSession);
        }
      }
    };

    checkExistingSession();
  }, []);

  // Ensure the Keap script is loaded
  useEffect(() => {
    if (!document.getElementById("keap-payment-method-script")) {
      const script = document.createElement("script");
      script.id = "keap-payment-method-script";
      script.src = "https://api.infusionsoft.com/payment-method/bundle/payment-method-embed.js";
      script.async = true;
      document.head.appendChild(script);

      console.log("Keap payment script added to the page");
    }
  }, []);

  const handleKeapPaymentSubmit = creditCardId => {
    console.log("Received credit card ID:", creditCardId);
    setKeapCreditCardId(creditCardId);

    // Find the newly added card in the refreshed cards list
    const newlyAddedCard = UserStore.cards?.find(card => card.id === creditCardId);

    if (newlyAddedCard) {
      // Use the actual card object from the API
      setSelectedCard(newlyAddedCard);
    } else {
      // Fallback to a virtual card object if not found
      const virtualCard = {
        id: creditCardId,
        isKeap: true,
        label: "New Card"
      };
      setSelectedCard(virtualCard);
    }
  };

  // Listen for Keap payment method messages
  useEffect(() => {
    const handleKeapResponse = event => {
      // Only process messages with Keap payment-related data
      if (event.data && typeof event.data === "object" && "success" in event.data) {
        if (event.data.success) {
          console.log("Payment method created successfully:", event.data);

          Sentry.addBreadcrumb({
            category: "payment",
            message: "Keap payment success message received",
            level: "info",
            data: {
              creditCardId: event.data.creditCardId,
              timestamp: new Date().toISOString()
            }
          });

          // Log creditCardId which is what the backend expects
          console.log("[KEAP] Credit card ID received:", {
            creditCardId: event.data.creditCardId,
            fullEventData: event.data
          });

          setKeapCreditCardId(event.data.creditCardId);
        } else {
          console.error("Keap payment error:", event.data);

          Sentry.captureMessage("Keap payment error message received", {
            level: "error",
            extra: {
              keapErrorData: event.data,
              timestamp: new Date().toISOString()
            }
          });

          setCheckoutError("There was an issue processing your payment. Please try again.");
        }
      }
    };

    window.addEventListener("message", handleKeapResponse);
    return () => window.removeEventListener("message", handleKeapResponse);
  }, []);

  const updateSelectedCard = selectedCardLabel => {
    console.log("[CARD_SELECTION] User selected card:", {
      selectedCardLabel,
      availableCards: cards?.map(c => ({ label: c.label, id: c.id })) || [],
      isNewCard: selectedCardLabel === NEW_CARD_OPTION_LABEL
    });

    if (selectedCardLabel === NEW_CARD_OPTION_LABEL) {
      setSelectedCard(NEW_CARD_OPTION_LABEL);
      setEnteringNewCard(true);
    } else {
      const foundCard = cards?.find(c => c?.label === selectedCardLabel);
      console.log("[CARD_SELECTION] Found card object:", {
        requestedLabel: selectedCardLabel,
        foundCard: foundCard,
        foundCardId: foundCard?.id
      });
      setSelectedCard(foundCard);
    }
  };

  const hasProductsToShip = !!CartStore?.productItemsReadyForCheckout
    ?.filter(filterOutShippingProducts)
    ?.filter(filterOutLateFeeProducts)
    ?.filter(filterOutTaxProducts)?.length;

  let checkoutEnabled = false;
  if (!hasProductsToShip) checkoutEnabled = true;
  const hasValidCard = selectedCard === NEW_CARD_OPTION_LABEL ? cardValid : !!selectedCard;
  checkoutEnabled = hasValidCard && agreeToTerms && agreeToMaterialsDelivery;

  // Helper function to handle successful registration
  const handleSuccessfulRegistration = result => {
    try {
      const { thankYouParams } = result;
      let params;
      if (thankYouParams) {
        params = btoa(JSON.stringify(thankYouParams));
      }

      Sentry.addBreadcrumb({
        category: "checkout",
        message: "Navigating to thank you page",
        level: "info",
        data: {
          transactionId: result.transactionId,
          cached: result.cached
        }
      });

      navigate(`/thankyou${params ? `?p=${params}` : ""}`);
      setTimeout(() => CartStore.clearEnrollmentsAfterCheckOut(), 100);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "handle_successful_registration" }
      });
    }
  };

  const completePurchase = async () => {
    const now = Date.now();
    const sessionId = now + "-" + Math.random().toString(36).substring(2, 11);

    // Prevent double-clicks
    if (now - lastClickTimeRef.current < 200) {
      console.warn("[CHECKOUT] Double-click detected - ignoring");
      return;
    }
    lastClickTimeRef.current = now;

    // Log checkout attempt
    Sentry.addBreadcrumb({
      category: "checkout",
      message: "Checkout button clicked",
      level: "info",
      data: {
        sessionId,
        processing,
        checkoutEnabled,
        selectedCard: selectedCard?.id || selectedCard,
        enrollmentCount: enrollmentsReadyForCheckout?.length,
        cartTotal: CartStore?.total,
        timestamp: new Date().toISOString()
      }
    });

    if (processing) {
      Sentry.captureMessage("Checkout clicked while already processing", {
        level: "warning",
        extra: { sessionId, processing: true }
      });
      return;
    }

    setProcessing(true);

    // Log processing state change
    Sentry.addBreadcrumb({
      category: "checkout",
      message: "Processing state set to true",
      level: "info",
      data: { sessionId }
    });

    try {
      let card = selectedCard;

      // If using a new card, get the payment method ID from Keap
      if (selectedCard === NEW_CARD_OPTION_LABEL) {
        // Find the Keap payment element
        const keapElement = document.getElementById("keap-payment-method");

        if (keapElement) {
          // Log Keap payment attempt
          Sentry.addBreadcrumb({
            category: "payment",
            message: "Submitting Keap payment form",
            level: "info",
            data: { sessionId }
          });

          // Submit the form
          keapElement.submit();

          // Wait for the credit card ID (handled by event listener)
          let attempts = 0;
          while (!keapCreditCardId && attempts < 10) {
            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;

            // Log waiting attempts
            if (attempts % 3 === 0) {
              Sentry.addBreadcrumb({
                category: "payment",
                message: "Waiting for Keap payment response",
                level: "info",
                data: { sessionId, attempts }
              });
            }
          }

          if (keapCreditCardId) {
            card = { id: keapCreditCardId, isKeap: true };

            console.log("[KEAP] Using creditCardId from Keap:", {
              sessionId,
              creditCardId: keapCreditCardId,
              creditCardIdType: typeof keapCreditCardId,
              attempts,
              totalWaitTime: attempts * 500
            });

            Sentry.addBreadcrumb({
              category: "payment",
              message: "Keap creditCardId received",
              level: "info",
              data: { sessionId, attempts, creditCardId: keapCreditCardId }
            });
          } else {
            console.error("[KEAP] Payment method creation timed out:", {
              sessionId,
              attempts,
              totalWaitTime: attempts * 500,
              keapElementFound: !!keapElement
            });

            Sentry.captureMessage("Keap payment timeout", {
              level: "error",
              extra: {
                sessionId,
                attempts,
                timeout: true,
                totalWaitTime: attempts * 500,
                keapElementFound: !!keapElement
              }
            });

            setCheckoutError(
              "Looks like we had an issue processing your card. Please check your card details and try again."
            );
            setProcessing(false);
            return;
          }
        } else {
          console.error("[KEAP] Payment element not found in DOM:", {
            sessionId,
            elementId: "keap-payment-method"
          });

          Sentry.captureMessage("Keap payment element not found", {
            level: "error",
            extra: { sessionId }
          });

          setCheckoutError("Payment form not found. Please try refreshing the page.");
          setProcessing(false);
          return;
        }
      }

      // Enhanced pre-checkout logging for diagnostics
      console.log("[CHECKOUT] Final card validation before API call:", {
        sessionId,
        cardId: card?.id,
        cardType: typeof card?.id,
        cardObject: card,
        selectedCardType: typeof selectedCard,
        isNewCardFlow: selectedCard === NEW_CARD_OPTION_LABEL,
        keapCreditCardId,
        authContactId: AuthStore?.infusionsoftId,
        timestamp: new Date().toISOString()
      });

      // Basic card ID validation - prevent obviously invalid requests
      if (checkoutEnabled && (!card?.id || card.id === null || card.id === undefined || card.id === "")) {
        console.error("[CHECKOUT] Invalid card ID detected:", {
          sessionId,
          cardObject: card,
          selectedCard,
          cardId: card?.id,
          cardType: typeof card?.id
        });

        Sentry.captureMessage("Invalid card ID detected before checkout", {
          level: "error",
          extra: {
            sessionId,
            cardObject: card,
            selectedCard,
            cardId: card?.id,
            cardType: typeof card?.id,
            authContactId: AuthStore?.infusionsoftId
          }
        });

        setCheckoutError("Payment method validation failed. Please try again.");
        setProcessing(false);
        return;
      }

      Sentry.addBreadcrumb({
        category: "payment",
        message: "Final payment validation before API call",
        level: "info",
        data: {
          sessionId,
          cardId: card?.id,
          cardType: typeof card?.id,
          cardSource: selectedCard === NEW_CARD_OPTION_LABEL ? "keap_new" : "existing",
          authContactId: AuthStore?.infusionsoftId,
          keapTimeoutOccurred: selectedCard === NEW_CARD_OPTION_LABEL && !keapCreditCardId,
          selectedCardLabel: typeof selectedCard === "string" ? selectedCard : selectedCard?.label,
          availableCardsCount: cards?.length || 0
        }
      });

      const result = await CartStore?.checkOut(card, sessionId);

      // Get the current idempotency key from RegistrationService
      const registrationState = RegistrationService.getState();

      // Log checkout result with idempotency key
      console.log("[CHECKOUT] V2 API response received:", {
        sessionId,
        idempotencyKey: registrationState.idempotencyKey,
        success: result.success,
        error: result.error,
        errorType: result.errorType,
        canRetry: result.canRetry,
        cached: result.cached,
        transactionId: result.transactionId
      });

      Sentry.addBreadcrumb({
        category: "checkout",
        message: "V2 Checkout API response received",
        level: result.success ? "info" : "error",
        data: {
          sessionId,
          success: result.success,
          errorType: result.errorType,
          canRetry: result.canRetry,
          cached: result.cached,
          transactionId: result.transactionId
        }
      });

      if (result.success) {
        // Clear processing state immediately on success
        setProcessing(false);
        setCheckoutError(null);

        // Show duplicate message if cached
        if (result.cached || result.isDuplicate) {
          setRegistrationStatus("This registration was already processed successfully.");
          // Still navigate to thank you page
          setTimeout(() => handleSuccessfulRegistration(result), 1500);
        } else {
          handleSuccessfulRegistration(result);
        }
      } else {
        // Handle error based on type - use userMessage which contains the API's message
        setCheckoutError(result.error);
        setErrorType(result.errorType);
        setCanRetry(result.canRetry || false);

        // If it's a concurrency error or DUPLICATE_IN_PROGRESS, poll for status
        if (result.errorType === "CONCURRENCY_ERROR" || result.error === "DUPLICATE_IN_PROGRESS") {
          setRegistrationStatus("Registration already in progress. Checking status...");
          // Use the idempotency key from the error response if available
          const pollResult = await RegistrationService.pollRegistrationStatus(result.idempotencyKey);
          if (pollResult.success) {
            handleSuccessfulRegistration(pollResult);
          } else {
            setCheckoutError(pollResult.error || "Registration failed");
            setCanRetry(pollResult.canRetry || false);
          }
        }
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          sessionId,
          context: "checkout_completePurchase",
          cardId: selectedCard?.id || selectedCard,
          enrollmentCount: enrollmentsReadyForCheckout?.length
        }
      });
      setCheckoutError("An unexpected error occurred. Please try again.");
    } finally {
      setProcessing(false);

      // Log processing state reset
      Sentry.addBreadcrumb({
        category: "checkout",
        message: "Processing state set to false",
        level: "info",
        data: { sessionId }
      });
    }
  };

  const enrollmentItems = CartStore?.enrollmentsReadyForCheckout?.map(enrollment => (
    <EnrollmentItem {...enrollment} key={enrollment.id} />
  ));

  const actionButtonDisabled = processing;

  const actionButton = (
    <div className="checkout-wrapper">
      <div className="checkbox-row">
        <Checkbox value="I agree to the terms and conditions" selected={agreeToTerms} onChange={updateAgreeToTerms} />
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.letsplaymusicsite.com/enrollmentdisclaimer?srsltid=AfmBOooiymXH63uT7SQTJ1GmH3MCJIMlre66Q9-fOwRyJsAn-0iewMre"
          className="link"
        >
          View the terms here
        </a>
      </div>
      <div className="materials-checkbox-row">
        <Checkbox
          value="I understand that materials will be delivered to my teacher who will distribute them at the beginning of class"
          selected={agreeToMaterialsDelivery}
          onChange={updateAgreeToMaterialsDelivery}
        />
      </div>
      {registrationStatus && (
        <div className="registration-status" style={{ color: "#0066cc", marginBottom: "10px" }}>
          {registrationStatus}
        </div>
      )}
      {/* Error is displayed in PaymentInfoSection, don't show it here to avoid duplication */}
      <CheckoutButton
        action={completePurchase}
        disabled={actionButtonDisabled || processing}
        processing={processing}
        hasProductsToShip={hasProductsToShip}
        canRetry={canRetry}
        errorType={errorType}
      />
      {/* <div className="terms-and-conditions-disclaimer">
        By completing your purchase, you agree to the following{" "}
        <a
          className="terms-and-conditions-link"
          href="https://www.new.letsplaymusicsite.com/enrollmentdisclaimer"
          target="_blank"
          rel="noopener noreferrer"
        >
          terms and conditions.
        </a>
      </div> */}
    </div>
  );

  const paymentInfoProps = {
    selectedCard,
    updateSelectedCard,
    enteringNewCard,
    setEnteringNewCard,
    newCardDetails,
    setNewCardDetails,
    actionButton,
    checkoutError,
    hasProductsToShip,
    onKeapPaymentSubmit: handleKeapPaymentSubmit,
    canRetry,
    errorType
  };
  const paymentInfoSection = <PaymentInfoSection {...paymentInfoProps} />;
  const lateFeesItem = CartStore?.lateFeeTotal ? <LateFeeItem /> : null;
  const shippingItem = hasProductsToShip ? <ShippingItem /> : null;
  const taxItem = CartStore?.AZTaxPercentage ? <AZTaxItem /> : null;

  const couponItem = CartStore?.enrollmentsReadyForCheckout?.length ? <CouponItem /> : null;

  const headerLabel = hasProductsToShip ? "What I'm Purchasing" : "What I'm Enrolling In";

  return (
    <div className="checkout">
      {isMobile ? paymentInfoSection : null}
      <div className="col">
        <div className="col-header">{headerLabel}</div>
        <div className="enrollment-items-wrapper">
          {enrollmentItems}
          {shippingItem}
          {lateFeesItem}
          {taxItem}
          {couponItem}
        </div>
      </div>
      {isMobile ? null : paymentInfoSection}
      {isMobile ? actionButton : null}
    </div>
  );
});

export default Checkout;
